import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles, ArrowRight, Check,
  Crown, Coins, TrendingUp, Boxes, ShieldAlert, Radar,
  MessageSquare, Truck, BookOpen, Building2, Database, ChevronDown,
} from 'lucide-react';
import Logo from '../components/Logo';
import heroMascot from '../assets/humanoid_original_download.png';
import { api } from '../lib/api';

/* ── 12 features with per-block brand accent color ── */
const FEATURES = [
  { id: 'ai-ceo', Icon: Crown, title: 'AI CEO', desc: 'Analyzes long-term goals. Your strategic partner for expansion and big-picture decisions.', accent: '#8B5CF6' },
  { id: 'ai-cfo', Icon: Coins, title: 'AI CFO', desc: 'Focuses on cash flow and profitability. Automated financial oversight to ensure you never run out of cash.', accent: '#14B8A6' },
  { id: 'ai-sales', Icon: TrendingUp, title: 'AI Sales', desc: 'Optimizes customer leads. Convert more inquiries into sales with AI-driven follow-up advice.', accent: '#F59E0B' },
  { id: 'ai-inventory', Icon: Boxes, title: 'AI Inventory', desc: 'Predicts stock-outs. Never lose a sale because of empty shelves.', accent: '#6366F1' },
  { id: 'ai-fraud', Icon: ShieldAlert, title: 'AI Fraud', desc: 'Flags suspicious transactions (unusual times/volumes). Stop internal theft and billing errors before they hit your ledger.', accent: '#EF4444' },
  { id: 'ai-market', Icon: Radar, title: 'AI Market', desc: 'Scrapes competitor prices (Amazon/Flipkart). Stay competitive with real-time alerts when rivals drop their prices.', accent: '#06B6D4' },
  { id: 'ai-reactive', Icon: MessageSquare, title: 'AI Reactive', desc: "Standard 24/7 Q&A. Instant answers to any business question: 'How much did we sell today?'", accent: '#EC4899' },
  { id: 'ai-purchase', Icon: Truck, title: 'AI Purchase', desc: 'Manages vendors. Buy smarter\u2014AI tells you which supplier is cheapest this week.', accent: '#10B981' },
  { id: 'ai-proactive', Icon: Sparkles, title: 'AI Proactive', desc: 'Gives advice without being asked. A manager that pokes you with growth tips every morning.', accent: '#38BDF8' },
  { id: 'smart-erp', Icon: BookOpen, title: 'Smart ERP Engine', desc: 'A professional, Desktop-First Accounting ERP designed for high-performance business management.', accent: '#2563EB' },
  { id: 'multi-branch', Icon: Building2, title: 'Multi-Branch Ledger', desc: 'Consolidated financial and stock view supporting organizations with 100+ branches.', accent: '#F43F5E' },
  { id: 'data-bridge', Icon: Database, title: 'Universal Data Bridge', desc: 'Integration hub with built-in Mapper for Tally, Zoho, and Excel for instant data migration.', accent: '#84CC16' },
];

/* ── Region presets — Phase 20b ── */
const STORAGE_KEY = 'landing_region_country';
const DEFAULT_COUNTRY = 'IN';

const FALLBACK_PRESETS = [
  { country_code: 'IN', country_name: 'India',                currency_code: 'INR', currency_symbol: '₹',    tax_label: 'GST', tax_id_label: 'GSTIN', tax_rate_default: 18, flag: '🇮🇳' },
  { country_code: 'AE', country_name: 'United Arab Emirates', currency_code: 'AED', currency_symbol: 'د.إ',  tax_label: 'VAT', tax_id_label: 'TRN',   tax_rate_default: 5,  flag: '🇦🇪' },
  { country_code: 'SA', country_name: 'Saudi Arabia',         currency_code: 'SAR', currency_symbol: '﷼',    tax_label: 'VAT', tax_id_label: 'TRN',   tax_rate_default: 15, flag: '🇸🇦' },
  { country_code: 'QA', country_name: 'Qatar',                currency_code: 'QAR', currency_symbol: '﷼',    tax_label: 'VAT', tax_id_label: 'TRN',   tax_rate_default: 5,  flag: '🇶🇦' },
  { country_code: 'OM', country_name: 'Oman',                 currency_code: 'OMR', currency_symbol: '﷼',    tax_label: 'VAT', tax_id_label: 'TRN',   tax_rate_default: 5,  flag: '🇴🇲' },
  { country_code: 'BH', country_name: 'Bahrain',              currency_code: 'BHD', currency_symbol: '.د.ب', tax_label: 'VAT', tax_id_label: 'TRN',   tax_rate_default: 10, flag: '🇧🇭' },
  { country_code: 'KW', country_name: 'Kuwait',               currency_code: 'KWD', currency_symbol: 'د.ك',  tax_label: 'VAT', tax_id_label: 'TRN',   tax_rate_default: 0,  flag: '🇰🇼' },
];

function formatLandingMoney(n, preset) {
  if (!Number.isFinite(n)) return `${preset.currency_symbol}0`;
  const abs = Math.abs(n);
  const grouped = preset.currency_code === 'INR'
    ? abs.toLocaleString('en-IN')
    : abs.toLocaleString('en-US');
  return `${n < 0 ? '-' : ''}${preset.currency_symbol}${grouped}`;
}

function loadStoredCountry() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && FALLBACK_PRESETS.some(p => p.country_code === v)) return v;
  } catch { /* silent */ }
  return DEFAULT_COUNTRY;
}

function saveCountry(code) {
  try { localStorage.setItem(STORAGE_KEY, code); } catch { /* silent */ }
}

const PRICING_CURRENCY_KEY = 'aibm_preferred_currency';
const CURRENCY_TO_COUNTRY = { INR: 'IN', AED: 'AE', SAR: 'SA', QAR: 'QA', OMR: 'OM', BHD: 'BH', KWD: 'KW', USD: 'OTHER' };
const PRICING_CURRENCIES = [
  { code: 'INR', symbol: '₹', label: 'INR' },
  { code: 'AED', symbol: 'AED ', label: 'AED' },
  { code: 'SAR', symbol: 'SAR ', label: 'SAR' },
  { code: 'QAR', symbol: 'QAR ', label: 'QAR' },
  { code: 'OMR', symbol: 'OMR ', label: 'OMR' },
  { code: 'BHD', symbol: 'BHD ', label: 'BHD' },
  { code: 'KWD', symbol: 'KWD ', label: 'KWD' },
  { code: 'USD', symbol: '$', label: 'USD' },
];
const fmtPlanPrice = (code, val) => {
  const c = PRICING_CURRENCIES.find(x => x.code === code) || { symbol: code + ' ' };
  return `${c.symbol}${Number(val || 0).toLocaleString(code === 'INR' ? 'en-IN' : 'en-US')}`;
};
const fmtLandingLimit = (v) => (v === -1 || v === null || v === undefined) ? 'Unlimited' : String(v);

const PLAN_HIGHLIGHTS = { basic: 'Free forever', pro: 'Most popular', premium: 'Best value' };
const PLAN_TAGLINES = { basic: "Log your business. That's it.", standard: 'For the growing shop.', pro: 'Let the AI run your day.', premium: 'The whole show.' };
const PLAN_CTAS = { basic: 'Get started free', standard: 'Start free trial', pro: 'Start free trial', premium: 'Start free trial' };
const PLAN_ROUTES = { basic: '/signup?plan=basic', standard: '/signup?trial=premium', pro: '/signup?trial=premium', premium: '/signup?trial=premium' };

/* ── Feature card component ── */
function FeatureCard({ f }) {
  const c = f.accent;
  return (
    <div
      data-testid={`landing-block-${f.id}`}
      className="relative glass-card glass-sheen rounded-3xl p-5 lg:p-6 flex flex-col h-full transition-all duration-200 hover:scale-[1.01] feature-card-hover"
      style={{
        background: `linear-gradient(180deg, rgba(255,255,255,0.04) 0%, ${c}08 100%)`,
        border: `1px solid ${c}30`,
        '--card-accent': c,
      }}
    >
      {/* Icon chip — solid gradient using the block's accent */}
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 flex-shrink-0"
        style={{
          background: `linear-gradient(135deg, ${c}20, ${c}40)`,
          boxShadow: `0 4px 12px ${c}30`,
        }}
      >
        <f.Icon size={20} style={{ color: c }} strokeWidth={1.8} />
      </div>
      <h3 className="text-[15px] font-bold text-white tracking-tight leading-snug">{f.title}</h3>
      <p className="mt-2 text-[13px] text-white/50 leading-[1.45] flex-1">{f.desc}</p>
    </div>
  );
}

export default function Landing() {
  // Phase 20b — region selection
  const [presets, setPresets] = useState(FALLBACK_PRESETS);
  const [country, setCountry] = useState(() => loadStoredCountry());

  // Dynamic pricing state
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(false);
  const [featureCatalog, setFeatureCatalog] = useState(null);
  const [pricingCurrency, setPricingCurrency] = useState(() => {
    try { return localStorage.getItem(PRICING_CURRENCY_KEY) || null; } catch { return null; }
  });
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [effectivePricesMap, setEffectivePricesMap] = useState(null); // {slug: {source, monthly, annual}}

  useEffect(() => {
    let mounted = true;
    api.regionsPresets()
      .then(r => {
        if (mounted && Array.isArray(r?.items) && r.items.length === 7) {
          setPresets(r.items);
        }
      })
      .catch(() => { /* keep fallback */ });
    return () => { mounted = false; };
  }, []);

  // Fetch plans + geo + feature catalog — re-run when currency changes to get country overrides
  useEffect(() => {
    let mounted = true;
    const BASE = process.env.REACT_APP_BACKEND_URL || '';
    (async () => {
      try {
        const countryCode = pricingCurrency ? CURRENCY_TO_COUNTRY[pricingCurrency] : null;
        const pkgUrl = countryCode
          ? `${BASE}/api/public/packages?country=${countryCode}`
          : `${BASE}/api/public/packages`;
        const [plansRes, geoRes, catRes] = await Promise.all([
          fetch(pkgUrl).then(r => r.ok ? r.json() : fetch(`${BASE}/api/public/packages`).then(r2 => r2.json())),
          fetch(`${BASE}/api/public/geo`).then(r => r.json()).catch(() => null),
          fetch(`${BASE}/api/public/features/catalog`).then(r => r.json()).catch(() => null),
        ]);
        if (!mounted) return;
        setPlans(plansRes.plans || []);
        setEffectivePricesMap(plansRes.effective_prices || null);
        if (catRes) setFeatureCatalog(catRes);
        // Auto-detect currency from geo, unless user has a saved preference
        if (!pricingCurrency) {
          const geoCurrency = geoRes?.currency || plansRes.default_currency || 'USD';
          const valid = (plansRes.currencies || ['INR','AED','QAR','USD']).includes(geoCurrency) ? geoCurrency : 'USD';
          setPricingCurrency(valid);
        }
      } catch {
        if (mounted) setPlansError(true);
      } finally { if (mounted) setPlansLoading(false); }
    })();
    return () => { mounted = false; };
  }, [pricingCurrency]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCurrencyChange = useCallback((code) => {
    setPricingCurrency(code);
    try { localStorage.setItem(PRICING_CURRENCY_KEY, code); } catch {}
  }, []);

  // Build feature key→label map
  const featureMap = useMemo(() => {
    const map = {};
    if (featureCatalog?.features) featureCatalog.features.forEach(f => { map[f.key] = f.label; });
    return map;
  }, [featureCatalog]);

  const activeCurrency = pricingCurrency || 'USD';

  const active = useMemo(
    () => presets.find(p => p.country_code === country) || presets[0] || FALLBACK_PRESETS[0],
    [presets, country],
  );

  const pickCountry = useCallback((code) => {
    setCountry(code);
    saveCountry(code);
  }, []);

  const withRegion = (route) => {
    const sep = route.includes('?') ? '&' : '?';
    return `${route}${sep}country=${encodeURIComponent(country)}`;
  };

  return (
    <div className="min-h-screen bg-dark-gradient" data-testid="landing-page">
      {/* Header */}
      <header
        className="sticky top-0 z-50 flex items-center justify-between px-6 lg:px-12 py-3 bg-navy-900/80 backdrop-blur-xl border-b border-white/6"
        data-testid="landing-header"
      >
        <div className="flex items-center gap-3">
          <Logo size="lg" theme="dark" shimmer />
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="landing-login-btn"
            className="h-10 px-5 rounded-full border-[1.5px] border-brand-teal bg-brand-teal/8 text-brand-teal text-sm font-bold flex items-center justify-center hover:bg-brand-teal/15 transition-colors"
          >
            Client Login
          </Link>
          <Link
            to={withRegion('/signup')}
            data-testid="landing-register-btn"
            className="h-10 px-5 rounded-full bg-gradient-to-r from-brand-purple to-brand-pink text-white text-sm font-bold flex items-center justify-center shadow-pink hover:opacity-90 transition-opacity"
          >
            Sign Up
          </Link>
        </div>
      </header>

      {/* Region Selector */}
      <div className="px-6 lg:px-12 pt-4 pb-1" data-testid="landing-region-selector">
        <p className="text-white/70 text-[11px] font-extrabold uppercase tracking-wider mb-2">Select Your Region</p>
        <div className="flex gap-2 flex-wrap">
          {presets.map(p => {
            const on = p.country_code === country;
            return (
              <button
                key={p.country_code}
                data-testid={`landing-region-${p.country_code}`}
                onClick={() => pickCountry(p.country_code)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm transition-all ${
                  on
                    ? 'bg-[#7C5CFF]/18 border-[#7C5CFF] shadow-[0_4px_14px_rgba(124,92,255,0.35)]'
                    : 'bg-white/[0.06] border-white/[0.14] hover:bg-white/[0.10]'
                }`}
              >
                <span className="text-base">{p.flag || p.country_code}</span>
                <span className={`text-xs font-bold tracking-wide ${on ? 'text-white' : 'text-white/80'}`}>{p.country_name || p.country_code}</span>
              </button>
            );
          })}
        </div>
        <p className="text-white/55 text-[11px] font-semibold mt-2" data-testid="landing-region-active">
          Showing prices in {active.currency_code} ({active.currency_symbol}) · {active.tax_label} {active.tax_rate_default}%
        </p>
      </div>

      {/* Hero */}
      <section className="flex flex-col items-center px-6 pt-16 pb-20 text-center" data-testid="landing-hero">
        <h1 className="text-4xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight max-w-xl mb-6" data-testid="landing-hero-headline">
          AI Business Intelligence Manager
        </h1>

        {/* Humanoid mascot in circular frame with float animation */}
        <div className="mascot-float my-12" data-testid="landing-mascot-stage">
          <div
            className="relative w-[280px] h-[280px] md:w-[380px] md:h-[380px] xl:w-[460px] xl:h-[460px] rounded-full overflow-hidden border border-white/10"
            style={{
              boxShadow: '0 0 60px rgba(139,92,246,0.25), 0 20px 40px rgba(0,0,0,0.35), inset 0 0 30px rgba(255,255,255,0.04)',
            }}
          >
            <img
              src={heroMascot}
              alt="1000AD AI concierge"
              className="w-full h-full object-contain"
              data-testid="landing-hero-mascot"
            />
          </div>
        </div>

        <p className="text-white/65 text-base lg:text-lg max-w-xl leading-relaxed">
          1000AD — the first AI-powered SaaS platform designed for the Modern Indian Entrepreneur.
        </p>

        <Link
          to={withRegion('/signup?trial=premium')}
          data-testid="landing-trial-cta"
          className="mt-8 h-16 px-8 rounded-full bg-gradient-to-r from-brand-purple to-brand-pink text-white font-extrabold text-base flex items-center gap-3 shadow-accent hover:opacity-95 transition-opacity"
        >
          <Sparkles size={20} />
          Start Your 14-Day Free Trial
          <ArrowRight size={20} />
        </Link>
        <p className="mt-3 text-white/45 text-xs tracking-wide">Full Premium features · No credit card required</p>
      </section>

      {/* Features Grid */}
      <section className="px-6 lg:px-12 py-16" data-testid="landing-features">
        <h2 className="text-2xl lg:text-3xl font-extrabold text-white text-center tracking-tight max-w-2xl mx-auto">
          Everything you need to run your business, in one AI-native platform
        </h2>
        <p className="text-white/50 text-sm text-center mt-3 mb-12">12 purpose-built modules working 24/7 so you can focus on growth.</p>
        <div
          className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6 max-w-[1320px] mx-auto"
          data-testid="landing-feature-grid"
        >
          {FEATURES.map(f => <FeatureCard key={f.id} f={f} />)}
        </div>
      </section>

      {/* Pricing */}
      <section className="px-6 lg:px-12 py-16" data-testid="landing-pricing-section">
        <h2 className="text-2xl lg:text-3xl font-extrabold text-white text-center tracking-tight">Simple, transparent pricing</h2>
        <p className="text-white/55 text-sm text-center mt-2 mb-6">All plans include {active.tax_label} invoicing, mobile app, and unlimited customers.</p>

        {/* Currency selector + Billing cycle toggle */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          {/* Desktop: chips; Mobile: dropdown */}
          <div className="hidden sm:flex rounded-full bg-white/[0.06] border border-white/[0.14] p-1" data-testid="pricing-currency-selector">
            {PRICING_CURRENCIES.map(c => {
              const on = c.code === activeCurrency;
              return (
                <button key={c.code} onClick={() => handleCurrencyChange(c.code)}
                  data-testid={`pricing-currency-${c.code}`}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-bold tracking-wide transition-all ${on ? 'bg-[#7C5CFF]/20 text-white shadow-[0_2px_8px_rgba(124,92,255,0.3)]' : 'text-white/60 hover:text-white/90'}`}
                >
                  {c.symbol.trim()} {c.code}
                </button>
              );
            })}
          </div>
          <div className="sm:hidden relative" data-testid="pricing-currency-dropdown">
            <select
              value={activeCurrency}
              onChange={e => handleCurrencyChange(e.target.value)}
              className="appearance-none bg-white/[0.08] border border-white/[0.14] text-white text-sm font-bold rounded-full px-5 py-2 pr-8 outline-none focus:border-[#7C5CFF]"
            >
              {PRICING_CURRENCIES.map(c => (
                <option key={c.code} value={c.code} className="bg-[#0F172A] text-white">
                  {c.symbol.trim()} {c.code}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" />
          </div>
          <div className="flex rounded-full bg-white/[0.06] border border-white/[0.14] p-1" data-testid="pricing-cycle-toggle">
            {['monthly', 'annual'].map(c => (
              <button key={c} onClick={() => setBillingCycle(c)}
                data-testid={`pricing-cycle-${c}`}
                className={`px-5 py-1.5 rounded-full text-xs font-bold tracking-wide transition-all ${billingCycle === c ? 'bg-brand-teal/20 text-brand-teal shadow-[0_2px_8px_rgba(20,184,166,0.3)]' : 'text-white/60 hover:text-white/90'}`}
              >
                {c === 'monthly' ? 'Monthly' : 'Annual'}
              </button>
            ))}
          </div>
        </div>

        {plansLoading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-white/50 text-sm">
            <div className="w-5 h-5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
            Loading plans...
          </div>
        ) : plansError ? (
          <div className="text-center py-12">
            <p className="text-white/50 text-sm mb-3">Could not load pricing. Please try again.</p>
            <button onClick={() => window.location.reload()} className="px-5 py-2 rounded-full border border-white/20 text-white text-sm font-bold hover:bg-white/10">Retry</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto" data-testid="landing-pricing">
            {plans.map(p => {
              const priceVal = p.prices?.[activeCurrency]?.[billingCycle] ?? 0;
              const isFree = priceVal === 0;
              const isPro = p.slug === 'pro';
              const highlight = PLAN_HIGHLIGHTS[p.slug];
              const tagline = PLAN_TAGLINES[p.slug] || '';
              const cta = PLAN_CTAS[p.slug] || 'Get started';
              const route = PLAN_ROUTES[p.slug] || '/signup';
              const effSource = effectivePricesMap?.[p.slug]?.source;
              const hasOverride = effSource === 'override' || effSource === 'mixed';
              return (
                <div
                  key={p.slug}
                  className={`relative glass-card rounded-3xl p-6 flex flex-col gap-3 ${isPro ? 'border-brand-teal ring-2 ring-brand-teal/20 shadow-teal' : ''}`}
                  data-testid={`landing-plan-${p.slug}`}
                >
                  {highlight && (
                    <span className={`absolute -top-3 right-5 px-3 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-wider shadow-lg ${isPro ? 'bg-brand-teal text-navy-900' : 'bg-white/10 border border-white/20 text-white'}`}>
                      {highlight}
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <p className="text-white/80 text-sm font-bold tracking-wider uppercase">{p.name}</p>
                    {hasOverride && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-500/15 text-emerald-400" data-testid={`country-pricing-${p.slug}`}>Country pricing</span>}
                  </div>
                  <div className="flex items-end gap-1">
                    <span className="text-white text-4xl font-extrabold tracking-tight tabular-nums" data-testid={`landing-price-${p.slug}`}>
                      {isFree ? 'Free' : fmtPlanPrice(activeCurrency, priceVal)}
                    </span>
                    {!isFree && <span className="text-white/55 text-sm mb-2">/ {billingCycle === 'monthly' ? 'mo' : 'yr'}</span>}
                  </div>
                  <p className="text-white/60 text-sm">{tagline}</p>

                  {/* Features from API with human labels */}
                  <div className="flex flex-col gap-2.5 mt-3 mb-2">
                    {(p.features || []).slice(0, 6).map(fKey => (
                      <div key={fKey} className="flex items-start gap-2.5">
                        <div className="w-5 h-5 rounded-full bg-emerald-500/12 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <Check size={12} className="text-emerald-500" strokeWidth={2.8} />
                        </div>
                        <span className="text-white/80 text-sm">{featureMap[fKey] || fKey}</span>
                      </div>
                    ))}
                    {(p.features || []).length > 6 && (
                      <p className="text-white/40 text-xs pl-7">+{p.features.length - 6} more features</p>
                    )}
                  </div>

                  {/* Limits compact */}
                  {p.limits && (
                    <div className="text-[10px] text-white/35 tabular-nums px-0.5 mb-2">
                      Users: {fmtLandingLimit(p.limits.max_users)} · Products: {fmtLandingLimit(p.limits.max_products)} · Invoices/mo: {fmtLandingLimit(p.limits.max_invoices_per_month)} · AI/mo: {fmtLandingLimit(p.limits.max_ai_requests_per_month)}
                    </div>
                  )}

                  <Link
                    to={withRegion(route)}
                    data-testid={`landing-plan-cta-${p.slug}`}
                    className={`mt-auto h-12 rounded-2xl flex items-center justify-center font-bold text-sm transition-opacity hover:opacity-90 ${
                      isPro
                        ? 'bg-gradient-to-r from-brand-teal to-brand-teal-deep text-white shadow-teal'
                        : `border border-white/20 text-white ${p.slug === 'premium' ? 'border-brand-pink text-pink-300' : ''}`
                    }`}
                  >
                    {cta}
                  </Link>
                </div>
              );
            })}
          </div>
        )}
        {activeCurrency !== 'INR' && (
          <p className="text-white/60 text-[11px] text-center mt-3 italic" data-testid="landing-fx-footnote">
            Prices displayed in {activeCurrency} for illustration; final billing in INR at checkout.
          </p>
        )}
      </section>

      {/* Footer */}
      <footer className="py-16 flex flex-col items-center gap-6" data-testid="landing-footer">
        <Link
          to={withRegion('/signup')}
          data-testid="landing-footer-cta"
          className="h-14 px-8 rounded-full bg-gradient-to-r from-brand-purple to-brand-pink text-white font-extrabold text-base flex items-center gap-3 shadow-accent hover:opacity-95 transition-opacity"
        >
          <Sparkles size={18} />
          Get Started
          <ArrowRight size={18} />
        </Link>
        <p className="text-white/35 text-xs tracking-wider flex items-center gap-1.5">AI-Powered Intelligence &amp; Management · © <Logo size="md" theme="dark" /></p>
      </footer>
    </div>
  );
}
